/**
 * BOGO Cart Popup — opens a modal when a customer adds a BOGO trigger product to cart,
 * letting them pick the free reward item (with variant picker if the reward has options).
 *
 * Strategy:
 *   1. Monitor cart-add activity via several signals (whichever fires first wins):
 *        (a) Subscribe to Dawn's pub/sub `cart-update` event
 *        (b) Patch window.fetch to intercept POST /cart/add.js responses
 *        (c) Monitor /cart.js for new line items (fallback when cart-update isn't dispatched)
 *        (d) Capture-phase click / submit interceptors for app-rendered add buttons (Boost SD)
 *   2. For each newly-added line item, resolve its BOGO data (page meta tags, or the
 *      window.__BOGO_TRIGGERS map injected from the shop metafield) — if active, open the popup.
 *   3. Popup loads pickable products via /products/{handle}.js (lazy, cached).
 *   4. Customer picks a product → if single-variant, add immediately; if multi-variant,
 *      show inline variant picker → add chosen variant.
 *   5. Buy-X-Get-Y (e.g. Buy 2 Get 1): the popup collects (X − units already in cart) paid
 *      "qualifying" picks first, then Y free picks, as numbered steps — so the customer
 *      leaves with a complete bundle instead of a "free" item that never qualified.
 *   6. Shopify's automatic BOGO discount applies the % off at the cart line.
 *
 * Anti-spam: a short cooldown after close prevents double-fire on rapid re-adds; the popup
 * re-opens normally on the next qualifying add.
 */

(function () {
  'use strict';

  const POPUP_ID = 'bogo-cart-popup';
  const NATIVE_CART_MODAL_ID = 'cls-cart-modal'; // the theme's "Added!" cart confirmation modal
  const REOPEN_COOLDOWN_MS = 2500; // short cooldown to prevent double-fire on rapid re-add

  // Cached metadata lookups
  const productCache = new Map();   // handle -> /products/{handle}.js JSON
  const noBogoCache = new Set();    // handles confirmed to NOT be BOGO triggers (avoid re-querying)

  let popupOpen = false;
  let lastClosedAt = 0;             // timestamp of last popup close (cooldown anchor)
  let currentTriggerHandle = null;
  let currentRewardHandles = [];
  let lastOpenArgs = null;          // {triggerHandle, bogoData, triggerProduct} — used to reopen the grid after an error
  let expectingBogoPopup = false;   // when true, MutationObserver kills native cart modal on insertion
  let currentPageBogo = null;       // {active, badge_text, reward_handles} read from THIS page's meta tags at init
  let currentPageHandle = null;     // product handle from URL (so we don't need /cart.js round-trip)
  let expressCheckoutSuppressUntil = 0; // when Shopify's "Buy It Now" / Dynamic Checkout is clicked, skip BOGO popup briefly (page is about to navigate)
  let pickPlan = null;              // multi-pick plan for Buy-X-Get-Y offers: {bogo, paid, free, units, done, adding, refined}
  let renderSeq = 0;                // guards against out-of-order grid renders

  // Read the current page's <meta name="bogo:*"> tags ONCE at init. Saves three fetches
  // (cart.js + /products/{h}.js + /products/{h}) when the customer adds the trigger product
  // from its own PDP — which is the most common path.
  function readCurrentPageBogoMeta() {
    const activeEl = document.querySelector('meta[name="bogo:active"]');
    if (!activeEl || activeEl.content !== 'true') return null;
    const text = (document.querySelector('meta[name="bogo:badge_text"]') || {}).content || 'Buy 1 Get 1 Free';
    const handlesRaw = (document.querySelector('meta[name="bogo:reward_handles"]') || {}).content || '[]';
    const excludedRaw = (document.querySelector('meta[name="bogo:excluded_variants"]') || {}).content || '[]';
    let handles = [], excluded = [];
    try { handles = JSON.parse(handlesRaw); } catch (e) {}
    try { excluded = JSON.parse(excludedRaw); } catch (e) {}
    return {
      active: true,
      badge_text: text,
      reward_handles: Array.isArray(handles) ? handles : [],
      excluded_variants: Array.isArray(excluded) ? excluded.map(String) : [],
    };
  }

  // Cache of reward-product excluded variants, keyed by handle. Populated lazily when the
  // popup needs to filter a reward's variant picker.
  const rewardExcludedCache = new Map();
  async function fetchRewardExcludedVariants(handle) {
    if (rewardExcludedCache.has(handle)) return rewardExcludedCache.get(handle);
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}`, { credentials: 'same-origin' });
      if (!res.ok) { rewardExcludedCache.set(handle, []); return []; }
      const html = await res.text();
      const m = html.match(/<meta\s+name="bogo:excluded_variants"\s+content='([^']*)'/i)
            || html.match(/<meta\s+name="bogo:excluded_variants"\s+content="([^"]*)"/i);
      let arr = [];
      if (m) {
        try { arr = JSON.parse(m[1].replace(/&quot;/g, '"')); } catch (e) {}
      }
      const ids = Array.isArray(arr) ? arr.map(String) : [];
      rewardExcludedCache.set(handle, ids);
      return ids;
    } catch (e) {
      rewardExcludedCache.set(handle, []);
      return [];
    }
  }

  // Quick lookup against /cart.js to see if a given product handle is currently in cart.
  // Used to decide whether to keep the popup open when a cart-add fails (the customer may
  // have the trigger already from a prior add).
  async function isHandleInCart(handle) {
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      return (cart.items || []).some(it => it && it.handle === handle);
    } catch (e) { return false; }
  }

  // Force the header's cart-icon-bubble to reflect current cart state. Optica's theme
  // doesn't expose Dawn's PUB_SUB_EVENTS on window, so product-form's cartUpdate publish
  // has no subscribers and the badge stays stale after every add.
  //
  // Approach: use Shopify's Section Rendering API to re-render the cart-icon-bubble
  // section and swap the existing element's contents. This works even when the cart
  // was EMPTY at page load — Dawn doesn't emit the .cart-count-bubble span in that
  // case, so a simple span-text-patch silently does nothing (the bug behind the
  // first-add miss). Falls back to a direct count patch if the section rendering
  // call fails for any reason.
  async function refreshCartIcon() {
    const existing = document.getElementById('cart-icon-bubble');
    if (!existing) return;
    try {
      const res = await fetch('/?sections=cart-icon-bubble', { credentials: 'same-origin' });
      if (res.ok) {
        const sections = await res.json();
        const html = sections && sections['cart-icon-bubble'];
        if (html) {
          // The section response wraps content in <div id="shopify-section-cart-icon-bubble">
          // (Shopify's section ID prefix). The page has <a id="cart-icon-bubble"> as the link;
          // their inner contents share the same SVG + .cart-count-bubble structure.
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const fresh = doc.getElementById('shopify-section-cart-icon-bubble')
                     || doc.getElementById('cart-icon-bubble');
          if (fresh) {
            existing.innerHTML = fresh.innerHTML;
            return;
          }
        }
      }
    } catch (e) {}
    // Fallback: direct /cart.js + span patch. Only helps if the bubble already exists.
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      const bubble = existing.querySelector('.cart-count-bubble');
      if (!bubble) return;
      const count = cart.item_count || 0;
      const visible = bubble.querySelector('span[aria-hidden="true"]');
      const sr = bubble.querySelector('span.visually-hidden');
      if (visible) visible.textContent = String(count);
      if (sr) sr.textContent = count + ' item' + (count === 1 ? '' : 's');
    } catch (e) {}
  }

  // Parse the variant id out of a /cart/add.js fetch init payload (works for both JSON body
  // and FormData body), then check it against the excluded list. Returns true if the variant
  // is excluded → caller should skip opening the BOGO popup for this add.
  function isAddVariantExcluded(fetchInit, excludedList) {
    if (!Array.isArray(excludedList) || !excludedList.length) return false;
    if (!fetchInit || !fetchInit.body) return false;
    let variantId = null;
    try {
      if (typeof fetchInit.body === 'string') {
        // Likely JSON: try parsing
        const parsed = JSON.parse(fetchInit.body);
        variantId = parsed.id || (parsed.items && parsed.items[0] && parsed.items[0].id);
      } else if (fetchInit.body instanceof FormData) {
        variantId = fetchInit.body.get('id');
      } else if (fetchInit.body instanceof URLSearchParams) {
        variantId = fetchInit.body.get('id');
      }
    } catch (e) { return false; }
    if (!variantId) return false;
    // Excluded list contains full GIDs like "gid://shopify/ProductVariant/123"
    // while cart-add body has the numeric id "123". Match by suffix.
    const idStr = String(variantId);
    return excludedList.some(gid => String(gid).endsWith('/' + idStr) || String(gid) === idStr);
  }

  // Remove the theme's native "Added!" modal proactively. Called when BOGO popup opens.
  function suppressNativeCartModal() {
    const m = document.getElementById(NATIVE_CART_MODAL_ID);
    if (m) m.remove();
  }

  // Watch for the native cart modal being inserted. If we're expecting a BOGO popup,
  // remove it within the same paint frame so the customer never sees a flash.
  function installNativeModalKiller() {
    if (!('MutationObserver' in window)) return;
    const obs = new MutationObserver(muts => {
      if (!expectingBogoPopup) return;
      for (const m of muts) {
        for (const node of m.addedNodes) {
          if (!node || node.nodeType !== 1) continue;
          if (node.id === NATIVE_CART_MODAL_ID) { node.remove(); continue; }
          // Catch nested insertion (e.g. modal added inside another wrapper)
          if (node.querySelector && node.querySelector('#' + NATIVE_CART_MODAL_ID)) {
            const inner = node.querySelector('#' + NATIVE_CART_MODAL_ID);
            if (inner) inner.remove();
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // ── Init ──
  document.addEventListener('DOMContentLoaded', init);

  // ── Card-scanner badge injector ────────────────────────────────────────
  // Looks at every product link on the page (anywhere in the DOM — works with Boost search
  // results, app blocks, quick-add widgets, etc.) and injects a BOGO badge for products
  // listed in window.__BOGO_TRIGGERS.
  function badgeAllVisibleCards() {
    const triggers = window.__BOGO_TRIGGERS;
    if (!triggers || typeof triggers !== 'object') return;
    if (!Object.keys(triggers).length) return;
    // Skip self-references: if we're on a PDP, the badge for the current product is already
    // rendered by Liquid via main-product.liquid. The scanner should only badge OTHER cards
    // (related products, recommendations, etc.) — not duplicate the current product's badge
    // on a breadcrumb link or some other in-page reference.
    const currentHandle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || null;
    document.querySelectorAll('a[href*="/products/"]').forEach(a => {
      const m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      if (!m) return;
      if (currentHandle && m[1] === currentHandle) return; // skip self
      const trigger = triggers[m[1]];
      if (!trigger) return;
      // Walk up to the card container — try common wrappers
      const card = a.closest(
        '.card-wrapper, ' +
        '.boost-sd__product-card, .boost-sd__product-item, .boost-sd__product, ' +
        '[class*="product-item-wrapper"], .grid__item, .product-card, ' +
        '[class*="product-item"]'
      );
      if (!card) return;
      // Already badged? Check for ANY .bogo-badge — Liquid-rendered badges on native cards
      // (from card-product.liquid) shouldn't be duplicated by the scanner's own injection.
      if (card.querySelector('.bogo-badge')) return;
      // Ensure positioned parent for absolute badge
      const cs = getComputedStyle(card);
      if (cs.position === 'static') card.style.position = 'relative';
      // Inject the badge
      const badge = document.createElement('span');
      badge.className = 'bogo-badge bogo-badge--card-inject';
      const text = (trigger && trigger.badge_text) || 'BOGO';
      badge.innerHTML = '<span class="bogo-badge-icon" aria-hidden="true">&#127873;</span><span class="bogo-badge-text"></span>';
      badge.querySelector('.bogo-badge-text').textContent = text;
      card.appendChild(badge);
    });
  }

  // Find the product handle associated with a cart-add form. Works for Boost SD's quick-add,
  // Dawn's product-form, and most other patterns — falls back to walking up the DOM looking
  // for any `a[href*="/products/"]` link near the form.
  function getHandleForCartAddForm(form) {
    // 1. Look for an explicit handle data attribute on the form or its ancestors
    let el = form;
    while (el && el !== document.body) {
      if (el.dataset && el.dataset.productHandle) return el.dataset.productHandle;
      el = el.parentElement;
    }
    // 2. Walk up to the nearest product card container, then find its product link
    const card = form.closest(
      '.boost-sd__product-card, .boost-sd__product-item, .boost-sd__product-item-grid-view-layout-image, ' +
      '.card-wrapper, [class*="product-card"], [class*="product-item"], .grid__item'
    );
    if (card) {
      const link = card.querySelector('a[href*="/products/"]');
      if (link) {
        const m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
        if (m) return m[1];
      }
    }
    // 3. PDP fallback: button lives inside a <product-form> with no card ancestor
    //    (the main PDP add-to-cart, not a recommendation card). Use URL handle.
    //    Recommendation cards are caught by step 2 above via .card-wrapper.
    if (form.closest && form.closest('product-form')) {
      const urlMatch = (location.pathname || '').match(/\/products\/([^/?#]+)/);
      if (urlMatch) return urlMatch[1];
    }
    return null;
  }

  // For Boost SD's quick-add and other apps that use a click handler (not a form submit)
  // to add to cart: when the customer clicks "Add to cart" on a BOGO trigger product, fire
  // the popup IMMEDIATELY on click — before Boost runs its own add-to-cart + redirect to /cart.
  // The popup will be visible by the time Boost completes its add, even if Boost navigates
  // away after. We don't try to stop the add (Boost owns its own variant selection).
  function installCartAddClickInterceptor() {
    document.addEventListener('click', e => {
      const btn = e.target.closest(
        'button[name="add"], ' +                                    // standard Dawn add btn
        'button.product-form__submit, ' +
        '.boost-sd__btn-add-to-cart, ' +                            // Boost SD add-to-cart (popup + card)
        '.boost-sd__add-to-cart-btn, ' +                            // legacy / variant name (kept as defensive)
        '.boost-sd__quick-shop button, ' +
        '.boost-sd__quick-shop-popup__add-to-cart-button, ' +
        '.boost-sd__product-quick-view-add-to-cart'
      );
      if (!btn) return;

      // Find the product handle from the surrounding card / quick-shop popup
      const handle = getHandleForCartAddForm(btn);
      if (!handle) return;
      const trigger = window.__BOGO_TRIGGERS && window.__BOGO_TRIGGERS[handle];
      if (!trigger || !trigger.active) return;

      // BOGO trigger product! Open popup right now — no waiting for fetch. The customer's
      // click ALSO triggers Boost's own add-to-cart (we let it run); the popup is already
      // visible by the time Boost's redirect fires (if it does).
      if (popupOpen) return;
      if (Date.now() - lastClosedAt < REOPEN_COOLDOWN_MS) return;
      if (Date.now() < expressCheckoutSuppressUntil) return;
      expectingBogoPopup = true;
      const bogoData = {
        active: true,
        badge_text: trigger.badge_text || 'Buy 1 Get 1 Free',
        reward_handles: Array.isArray(trigger.reward_handles) ? trigger.reward_handles : [],
        excluded_variants: [],
      };
      // Pull a clean product title. Prefer trigger.title from the BOGO map (clean & accurate),
      // then a card-level product link (avoids Boost's variant-picker labels which live inside
      // the same card via .boost-sd__popup-select-option), then a handle-based fallback.
      let titleText = trigger.title;
      if (!titleText) {
        const card = btn.closest('.boost-sd__product-card, .boost-sd__product-item, .card-wrapper, .grid__item, [class*="product-card"], [class*="product-item"]');
        if (card) {
          // The product link's TITLE attribute or trimmed text — link is OUTSIDE the popup variant picker
          const productLink = card.querySelector('a[href*="/products/"]');
          if (productLink) {
            titleText = productLink.getAttribute('title') || productLink.textContent.trim();
          }
        }
      }
      if (!titleText) {
        // Final fallback: humanise the handle ("sana-magneto45" → "Sana Magneto45")
        titleText = handle.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
      }
      openPopup(handle, bogoData, { title: titleText });
      // Best-effort: strip the wrapping product-link anchor's href for ~150ms so the
      // click can't navigate via the anchor's default action. We DON'T call
      // e.preventDefault() — Boost SD's own click handler checks defaultPrevented and
      // would silently skip the cart-add if we set it.
      const productAnchor = btn.closest('a[href*="/products/"]');
      if (productAnchor) {
        const origHref = productAnchor.getAttribute('href');
        productAnchor.removeAttribute('href');
        setTimeout(() => {
          if (origHref && !productAnchor.getAttribute('href')) {
            productAnchor.setAttribute('href', origHref);
          }
        }, 150);
      }
      // Persist trigger state so we can re-open the popup on the next page if Boost SD
      // does a programmatic window.location='/cart' after its add resolves (the href
      // strip above doesn't catch that — it's a JS navigation, not an anchor click).
      // The /cart page's init() reads this on load.
      try {
        sessionStorage.setItem('bogo-pending-trigger', JSON.stringify({
          handle: handle,
          bogoData: bogoData,
          triggerProduct: { title: titleText },
          t: Date.now()
        }));
      } catch (err) {}
    }, true); // capture phase, fire before Boost's own click handlers
  }

  function installCartAddFormInterceptor() {
    document.addEventListener('submit', e => {
      const form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      const action = form.getAttribute('action') || '';
      if (!/\/cart\/add(\?|$|\/)/.test(action)) return;

      // Determine if this is a BOGO product. If not, let the native submit happen — we don't
      // want to change behaviour for non-BOGO products on this page.
      const handle = getHandleForCartAddForm(form);
      const trigger = handle && window.__BOGO_TRIGGERS && window.__BOGO_TRIGGERS[handle];
      if (!trigger || !trigger.active) return;

      // BOGO! Intercept the native submit and route through AJAX so the fetch patch fires.
      e.preventDefault();
      e.stopPropagation();

      // Pre-open the popup with placeholder text so customer sees immediate feedback —
      // the fetch patch's slow-path would also open it but with a small delay.
      if (!popupOpen && Date.now() - lastClosedAt > REOPEN_COOLDOWN_MS
          && Date.now() > expressCheckoutSuppressUntil) {
        expectingBogoPopup = true;
        const bogoData = {
          active: true,
          badge_text: trigger.badge_text || 'Buy 1 Get 1 Free',
          reward_handles: Array.isArray(trigger.reward_handles) ? trigger.reward_handles : [],
          excluded_variants: [],
        };
        // Pull product title from the card (e.g. the heading link in the same Boost wrapper)
        const card = form.closest('.boost-sd__product-card, .boost-sd__product-item, .card-wrapper, .grid__item, [class*="product-card"], [class*="product-item"]');
        const titleEl = card && (card.querySelector('.boost-sd__product-title, .card__heading, h3, h2') || card.querySelector('a[href*="/products/"]'));
        const titleText = (titleEl && titleEl.textContent.trim()) || handle;
        openPopup(handle, bogoData, { title: titleText });
      }

      // Convert the native form submission to a /cart/add.js fetch.
      const fd = new FormData(form);
      fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      })
        .then(r => r.json().catch(() => ({})))
        .then(data => {
          // Notify any cart-icon listeners that the cart changed
          try {
            if (window.PUB_SUB_EVENTS && window.publish) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bogo-form-interceptor', cartData: data });
            }
          } catch (e) {}
        })
        .catch(err => {
          console.error('BOGO cart-add form interceptor: AJAX failed', err);
          // Fall back to native submission so customer isn't stuck
          if (popupOpen) closePopup();
          form.submit();
        });
    }, true); // capture phase so we intercept before Boost's own handlers
  }

  function installCardScanner() {
    // Initial pass after DOM ready
    badgeAllVisibleCards();
    // Repeat passes as third-party widgets (Boost, quick-add) lazy-render
    [100, 400, 1000, 2500, 5000].forEach(ms => setTimeout(badgeAllVisibleCards, ms));
    // Re-run on any DOM insertion (Boost paginated loading, app blocks)
    if ('MutationObserver' in window) {
      let scheduled = false;
      const obs = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => { scheduled = false; badgeAllVisibleCards(); });
      });
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }

  function init() {
    const popup = document.getElementById(POPUP_ID);
    if (!popup) return; // snippet not rendered on this page (e.g. checkout)

    // Close handlers
    popup.querySelectorAll('[data-bogo-close]').forEach(el => {
      el.addEventListener('click', closePopup);
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && popupOpen) closePopup();
    });

    // Variant picker back button
    const back = popup.querySelector('[data-bogo-variant-back]');
    if (back) back.addEventListener('click', () => {
      hideVariantPicker();
    });

    // Reward card click handler (delegated)
    const grid = document.getElementById('bogo-cart-popup-grid');
    if (grid) grid.addEventListener('click', onRewardCardClick);

    const vbody = document.getElementById('bogo-variant-picker-body');
    if (vbody) vbody.addEventListener('click', onVariantPickerClick);

    // Read meta tags + URL handle FROM THIS PAGE so we can fire popup with zero round-trips
    currentPageBogo = readCurrentPageBogoMeta();
    const urlMatch = location.pathname.match(/\/products\/([^\/?]+)/);
    currentPageHandle = urlMatch ? urlMatch[1] : null;

    // Install the native-modal killer BEFORE any cart-add can fire
    installNativeModalKiller();

    // Detect Shopify's express checkout buttons ("Buy It Now", Shop Pay button, dynamic
    // checkout buttons). When clicked, the page navigates straight to /checkout — there's
    // no point opening the BOGO popup, the customer has decided to express-checkout.
    // We flag a short suppression window so the popup is skipped for the imminent /cart/add
    // call. Cleared on next page load.
    document.addEventListener('click', e => {
      const expressBtn = e.target.closest(
        '.shopify-payment-button__button, ' +     // Shopify Payments dynamic checkout
        '.shopify-payment-button__button--unbranded, ' +
        '[name="buy-now"], ' +
        '[data-shopify="payment-button"], ' +
        '.shop-pay-button'
      );
      if (expressBtn) {
        expressCheckoutSuppressUntil = Date.now() + 5000; // 5s window covers the navigation
      }
    }, true);

    // Scan the page for product cards and badge any that match window.__BOGO_TRIGGERS.
    // Works regardless of which Liquid template / app rendered the card (native, Boost,
    // app blocks, quick-add). Keeps re-scanning as third-party widgets lazy-render.
    installCardScanner();

    // Intercept NATIVE form submissions to /cart/add for BOGO products. Some apps (Boost
    // AI Search & Filter's quick-add modal, certain quick-buy widgets) submit the cart
    // form as a native HTML POST instead of AJAX — the browser then follows /cart/add's
    // 302 redirect to /cart, the BOGO popup never gets a chance. We convert to AJAX so
    // the fetch patch above can detect it. Non-BOGO products keep their native behavior.
    installCartAddFormInterceptor();

    // Click-level interceptor for apps that use a button click handler instead of a form
    // submit (Boost SD quick-add etc.). Opens the BOGO popup immediately on click, before
    // the app's own cart-add + post-add navigation runs.
    installCartAddClickInterceptor();

    // 1. Hook Dawn's pub/sub if present
    if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined' && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
    }

    // 2. Patch fetch to catch /cart/add.js calls
    patchFetchForCartAdd();

    // 3. Cross-page popup restore. If the customer clicked Add-to-cart on a BOGO trigger
    // moments ago and Boost SD (or any app) navigated the page before our popup could be
    // seen, the click interceptor stashed the trigger in sessionStorage. Re-open here.
    // Bounded to 10s so we don't refire on stale stored state from yesterday's session.
    try {
      const pending = JSON.parse(sessionStorage.getItem('bogo-pending-trigger') || 'null');
      if (pending && pending.handle && pending.bogoData && (Date.now() - pending.t) < 10000) {
        sessionStorage.removeItem('bogo-pending-trigger');
        // Tiny delay so the new page's DOM/styles paint before the modal mounts on top.
        setTimeout(function() { openPopup(pending.handle, pending.bogoData, pending.triggerProduct); }, 200);
      } else if (pending) {
        sessionStorage.removeItem('bogo-pending-trigger');
      }
    } catch (err) {}
  }

  // ── Signal: pub/sub event ──
  function handleCartUpdate(event) {
    // Dawn dispatches { source, productVariantId, cartData }
    const variantId = event && (event.productVariantId || (event.cartData && event.cartData.items && event.cartData.items.slice(-1)[0]?.variant_id));
    if (!variantId) {
      // Fallback: scan cart for any BOGO-active product that we haven't shown for
      scanCartForBogo();
      return;
    }
    checkVariantForBogo(variantId);
  }

  // ── Signal: fetch patch ──
  // When /cart/add.js fires AND the current page is a BOGO trigger, fire the popup
  // INSTANTLY using the in-page meta tags. No /cart.js or /products/{h}.js round-trip
  // needed — those are saved for the cross-page case (quick-add from collection, etc.).
  function patchFetchForCartAdd() {
    if (typeof window.fetch !== 'function') return;
    const orig = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);

      // FAST PATH: if we're on a BOGO PDP, suppress native modal + open popup right now
      // (synchronously, before the fetch even resolves). The Promise resolution can
      // confirm/adjust later but the popup is already up.
      const expressActive = Date.now() < expressCheckoutSuppressUntil;
      if (isAdd && currentPageBogo && currentPageHandle && !popupOpen
          && Date.now() - lastClosedAt > REOPEN_COOLDOWN_MS
          && !expressActive) {
        // Skip if the added variant is in the trigger product's excluded list. We read the
        // variant id from the request body (covers both Dawn's form-data and JSON POSTs).
        const excludedVariant = isAddVariantExcluded(init, currentPageBogo.excluded_variants);
        if (!excludedVariant) {
          expectingBogoPopup = true;
          // Pull product title from the visible H1 (most accurate, instant). Must target the
          // <h1> specifically — `.product__title` div now also contains the BOGO badge text,
          // so matching the parent div would include the badge in our subtitle.
          const h1 = document.querySelector('.product__title h1') || document.querySelector('main h1');
          const titleText = (h1 && h1.textContent.trim()) || (document.title || '').split(/\s[-–]\s/)[0] || 'this product';
          openPopup(currentPageHandle, currentPageBogo, { title: titleText });
        }
      }

      const result = orig.apply(this, arguments);
      if (isAdd) {
        result.then(async res => {
          if (!res || !res.ok) {
            // Add failed — but the customer might have the trigger product in cart already
            // (e.g. tried to add 2nd unit of a 1-stock item — the 2nd add fails but the 1st
            // is still in their cart and qualifies for the BOGO). Check before auto-closing.
            if (popupOpen && expectingBogoPopup && currentPageHandle) {
              const triggerInCart = await isHandleInCart(currentPageHandle);
              if (triggerInCart) {
                // Keep popup open. Update subtitle to acknowledge the stock situation.
                const subtitleEl = document.getElementById('bogo-cart-popup-subtitle');
                if (subtitleEl) {
                  let msg = 'You already have this item in your cart from before — that qualifies you for the BOGO! Pick a free reward below.';
                  // If the response has Shopify's error description, prepend it for clarity
                  try {
                    const data = await res.clone().json();
                    if (data && data.description) msg = `${data.description}\n\n${msg}`;
                  } catch (e) {}
                  subtitleEl.textContent = msg;
                }
                expectingBogoPopup = false;
                return;
              }
              closePopup();
            }
            expectingBogoPopup = false;
            return;
          }
          // The add landed — let an open multi-pick popup re-count qualifying units in the cart.
          if (popupOpen && pickPlan) refinePlanFromCart(true);
          // SLOW PATH: cross-page add (no meta tags on current page) — fall back to handle scan
          res.clone().json().then(data => {
            const items = data.items || (data.id ? [data] : []);
            items.forEach(it => {
              if (popupOpen) return; // fast path already opened it
              if (it && it.variant_id) checkVariantForBogo(it.variant_id);
              else if (it && it.handle) checkHandleForBogo(it.handle);
            });
          }).catch(() => {});
          // Reset flag after a moment (the modal-kill window has passed)
          setTimeout(() => { expectingBogoPopup = false; }, 1500);
        }).catch(() => { expectingBogoPopup = false; });
      }
      return result;
    };
  }

  // ── Lookups ──

  async function checkVariantForBogo(variantId) {
    if (popupOpen) return; // already shown for this add
    // We need the product handle from the variant. Pull from cart.js (cheap, cached).
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      const item = (cart.items || []).find(i => String(i.variant_id) === String(variantId));
      if (!item) return;
      checkHandleForBogo(item.handle, String(variantId));
    } catch (e) { /* network errors are non-fatal */ }
  }

  async function checkHandleForBogo(handle, variantId) {
    if (popupOpen) return;
    if (noBogoCache.has(handle)) return;
    // Short cooldown after last close to prevent flicker on rapid re-adds. NOT a session lock:
    // if the customer empties the cart and re-adds later, they get the popup again.
    if (Date.now() - lastClosedAt < REOPEN_COOLDOWN_MS) return;
    const product = await fetchProduct(handle);
    if (!product) return;
    // Liquid metafields aren't returned by /products/{handle}.js — so we look for the bogo
    // indicator via a different signal: render a hidden marker on the product page, OR
    // we ask the storefront for them via a separate request. Pragmatic: do a tiny GraphQL-style
    // check by HEADing the metafield URL pattern — but that doesn't exist on storefront.
    // Solution: theme injects a window.__BOGO_TRIGGERS map (filled from a Liquid section that
    // runs on every page). See bogo-cart-popup.liquid for the injection.
    //
    // For now, check the in-memory triggers map first, then fall back to a metafields fetch
    // via the JSON endpoint:
    let bogoData = checkInMemoryTriggers(handle);
    if (!bogoData) {
      bogoData = await fetchProductMetafields(handle);
    }
    if (!bogoData || !bogoData.active) {
      noBogoCache.add(handle);
      return;
    }
    openPopup(handle, bogoData, product);
  }

  // ── In-memory triggers map ──
  // Optional optimisation: the theme can inject `window.__BOGO_TRIGGERS = { handle: { active: true, badge_text, reward_handles: [...] } }`
  // for all bogo-active products to avoid the per-add metafield fetch. We tolerate its absence.
  function checkInMemoryTriggers(handle) {
    if (typeof window.__BOGO_TRIGGERS === 'object' && window.__BOGO_TRIGGERS && window.__BOGO_TRIGGERS[handle]) {
      const t = window.__BOGO_TRIGGERS[handle];
      return {
        active: !!t.active,
        badge_text: t.badge_text || 'Buy 1 Get 1 Free',
        reward_handles: Array.isArray(t.reward_handles) ? t.reward_handles : [],
        trigger_handles: Array.isArray(t.trigger_handles) ? t.trigger_handles : [],
        same_set: t.same_set,
        buy_quantity: t.buy_quantity,
        get_quantity: t.get_quantity,
      };
    }
    return null;
  }

  // ── Per-product metafield fetch ──
  // Shopify exposes metafields via a special endpoint: /products/{handle}.json — but that
  // requires "Storefront API" permissions which most stores don't enable by default.
  // Instead we use a small trick: fetch the product page HTML and parse the metafields out
  // of the JSON-LD or a custom <meta name="bogo:..."> tag we render from Liquid.
  // For zero-config setup, we hit the product page and look for our own marker tags.
  async function fetchProductMetafields(handle) {
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}`, { credentials: 'same-origin' });
      if (!res.ok) return null;
      const html = await res.text();
      const activeMatch = html.match(/<meta\s+name="bogo:active"\s+content="([^"]+)"/i);
      if (!activeMatch || activeMatch[1] !== 'true') return null;
      const textMatch = html.match(/<meta\s+name="bogo:badge_text"\s+content="([^"]*)"/i);
      const handlesMatch = html.match(/<meta\s+name="bogo:reward_handles"\s+content='([^']*)'/i)
                       || html.match(/<meta\s+name="bogo:reward_handles"\s+content="([^"]*)"/i);
      let handles = [];
      if (handlesMatch) {
        try { handles = JSON.parse(handlesMatch[1].replace(/&quot;/g, '"')); } catch (e) {}
      }
      return {
        active: true,
        badge_text: textMatch ? decodeHtmlEntities(textMatch[1]) : 'Buy 1 Get 1 Free',
        reward_handles: Array.isArray(handles) ? handles : [],
      };
    } catch (e) { return null; }
  }

  function decodeHtmlEntities(s) {
    const t = document.createElement('textarea');
    t.innerHTML = s;
    return t.value;
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const data = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' }).then(r => r.json());
      productCache.set(handle, data);
      return data;
    } catch (e) { return null; }
  }

  // ── Popup open/close ──

  // Parse "B2G1" / "Buy 2 Get 1 Free" style badge text into quantities. Fallback only — the
  // shop map's explicit buy_quantity / get_quantity win when present.
  function parseBxgyFromText(text) {
    const s = String(text || '');
    const m = s.match(/\bB\s*(\d{1,2})\s*G\s*(\d{1,2})\b/i)
           || s.match(/\bbuy\s*(\d{1,2})\b[^0-9]{0,16}\bget\s*(\d{1,2})\b/i);
    if (!m) return { buy: 0, get: 0 };
    return { buy: parseInt(m[1], 10) || 0, get: parseInt(m[2], 10) || 0 };
  }

  // Merge the Buy-X-Get-Y facts (buy_quantity / get_quantity / same_set / trigger_handles) into
  // the popup's bogo data. PDP meta tags and older trigger-map entries don't carry them, but the
  // shop-level map injected on every page (window.__BOGO_TRIGGERS) does once the dashboard has
  // refreshed it. Missing → defaults to Buy 1 Get 1, i.e. the classic single-pick behaviour.
  function withBxgyMeta(handle, bogoData) {
    const t = (window.__BOGO_TRIGGERS && window.__BOGO_TRIGGERS[handle]) || {};
    const src = bogoData || {};
    const badgeText = src.badge_text || t.badge_text || 'Buy 1 Get 1 Free';
    // Quantities: explicit data first (popup args, then the shop map); else parse the badge text
    // ("B2G1", "Buy 2 Get 1 Free") so a campaign whose map predates the quantity fields still
    // gets the right number of picks.
    const parsed = parseBxgyFromText(badgeText);
    const buy = Math.max(1, parseInt(src.buy_quantity || t.buy_quantity, 10) || parsed.buy || 1);
    const get = Math.max(1, parseInt(src.get_quantity || t.get_quantity, 10) || parsed.get || 1);
    const rewards = (Array.isArray(src.reward_handles) && src.reward_handles.length)
      ? src.reward_handles
      : (Array.isArray(t.reward_handles) ? t.reward_handles : []);
    const triggers = Array.isArray(src.trigger_handles)
      ? src.trigger_handles
      : (Array.isArray(t.trigger_handles) ? t.trigger_handles : []);
    const sameSet = src.same_set !== undefined ? !!src.same_set
      : (t.same_set !== undefined ? !!t.same_set : triggers.length === 0);
    return Object.assign({}, src, {
      active: true,
      badge_text: badgeText,
      reward_handles: rewards,
      trigger_handles: triggers,
      same_set: sameSet,
      buy_quantity: buy,
      get_quantity: get,
      excluded_variants: Array.isArray(src.excluded_variants) ? src.excluded_variants : [],
    });
  }

  // How many more picks this popup should collect, given what's in the cart.
  //   paid  = qualifying items still needed to reach the "Buy X" quantity
  //   free  = reward items the customer may take for this bundle
  //   units = qualifying units already counted in the cart (≥ 1: the add that opened us)
  // `cart` may be null (cart not fetched yet) — then we assume exactly the one unit just added.
  function computePlan(bogo, cart) {
    const buy = Math.max(1, parseInt(bogo.buy_quantity, 10) || 1);
    const get = Math.max(1, parseInt(bogo.get_quantity, 10) || 1);
    const items = (cart && Array.isArray(cart.items)) ? cart.items : [];
    const rewardSet = new Set(bogo.reward_handles || []);
    const triggerSet = bogo.same_set ? rewardSet : new Set(bogo.trigger_handles || []);
    const qtyIn = set => items.reduce((n, it) => n + ((it && set.has(it.handle)) ? (parseInt(it.quantity, 10) || 0) : 0), 0);

    if (bogo.same_set) {
      // Same-set offer (e.g. "buy 2 Bella, get 1 Bella free"): every eligible unit counts
      // toward a bundle of (buy + get) units; Shopify makes the cheapest `get` units free.
      const bundle = buy + get;
      const units = Math.max(1, qtyIn(rewardSet));
      const progress = units % bundle;
      if (progress === 0) return { paid: 0, free: 0, units };
      const paid = Math.max(0, buy - progress);
      const free = Math.max(0, Math.min(get, bundle - progress - paid));
      return { paid, free, units };
    }
    // Cross-set offer ("buy 2 frames, get a cleaner free"): triggers and rewards are counted apart.
    const tUnits = Math.max(1, qtyIn(triggerSet));
    const rUnits = qtyIn(rewardSet);
    const paid = (buy - (tUnits % buy)) % buy;
    const bundlesTarget = Math.floor(tUnits / buy) + (paid > 0 ? 1 : 0);
    const free = Math.max(0, Math.min(get, bundlesTarget * get - rUnits));
    return { paid, free, units: tUnits };
  }

  // The initial plan assumes the customer has exactly the unit they just added. Once the cart
  // reflects the add (or if they already had qualifying items / added qty 2 on the PDP), refine
  // the plan from /cart.js. Only while nothing has been picked yet — after the first pick we
  // track progress locally.
  // `force` (used when the fetch patch sees the add resolve) re-counts even after the timer
  // path already ran — the timer may have fired before a slow add reached the cart.
  async function refinePlanFromCart(force) {
    const plan = pickPlan;
    if (!plan || plan.done > 0 || plan.adding || (plan.refined && !force)) return;
    let cart = null;
    try { cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json()); } catch (e) { return; }
    if (!cart || pickPlan !== plan || plan.done > 0 || plan.adding) return;
    plan.refined = true;
    const next = computePlan(plan.bogo, cart);
    if (next.paid === plan.paid && next.free === plan.free && next.units === plan.units) return;
    plan.paid = next.paid;
    plan.free = next.free;
    plan.units = next.units;
    renderStep();
  }

  function ordinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  // Which products the customer may pick from for a PAID (qualifying) step.
  function paidPickHandles(bogo) {
    if (bogo.same_set) return bogo.reward_handles || [];
    return (bogo.trigger_handles && bogo.trigger_handles.length) ? bogo.trigger_handles : (bogo.reward_handles || []);
  }

  async function openPopup(triggerHandle, bogoData, triggerProduct) {
    const popup = document.getElementById(POPUP_ID);
    const grid = document.getElementById('bogo-cart-popup-grid');
    if (!popup || !grid) return;
    // Hide the native "Added!" cart modal so customer only sees the BOGO popup
    suppressNativeCartModal();
    popupOpen = true;
    currentTriggerHandle = triggerHandle;
    const bogo = withBxgyMeta(triggerHandle, bogoData);
    currentRewardHandles = bogo.reward_handles || [];
    lastOpenArgs = { triggerHandle, bogoData, triggerProduct }; // for "← Pick a different reward" recovery
    const initial = computePlan(bogo, null);
    pickPlan = {
      bogo,
      triggerHandle,
      triggerTitle: (triggerProduct && triggerProduct.title) || 'a product',
      paid: initial.paid,
      free: initial.free,
      units: initial.units,
      done: 0,
      adding: false,
      refined: false,
      paidPickPrices: [],   // unit prices (product .js scale) of the paid picks made in this popup
      cap: Infinity,        // same-set offers: the price above which a "free" pick isn't actually free
    };
    popup.hidden = false;
    document.body.style.overflow = 'hidden';

    // Refresh the header's cart icon to reflect the trigger product that was just added.
    // We don't know exactly when Boost SD / Dawn's add-to-cart completes server-side, so
    // poll twice: once after a fast network typically settles, once for slower networks.
    setTimeout(refreshCartIcon, 800);
    setTimeout(refreshCartIcon, 2500);
    // Fallback refine (the fetch patch also refines the moment the add resolves).
    setTimeout(refinePlanFromCart, 1500);

    await renderStep();
  }

  // Header: title / subtitle / step pills for the current pick.
  function updateHeader(plan, stepIsPaid) {
    const titleEl = document.getElementById('bogo-cart-popup-title');
    const subtitleEl = document.getElementById('bogo-cart-popup-subtitle');
    const stepsEl = document.getElementById('bogo-cart-popup-steps');
    const total = plan.paid + plan.free;
    const badge = plan.bogo.badge_text || 'BOGO';
    const freeWord = plan.free > 1 ? `${plan.free} FREE items` : 'FREE item';
    if (titleEl) {
      if (total === 0) titleEl.textContent = '\u{1F389} Offer complete!';
      else if (stepIsPaid) titleEl.textContent = `\u{1F6D2} Pick your ${ordinal(plan.units + plan.done + 1)} item`;
      else titleEl.textContent = `\u{1F381} Pick your ${freeWord}!`;
    }
    if (subtitleEl) {
      if (total === 0) {
        subtitleEl.textContent = `${badge} — you added ${plan.triggerTitle}. You already have enough items for this offer.`;
      } else if (stepIsPaid) {
        const left = plan.paid - plan.done;
        subtitleEl.textContent = `${badge} — you added ${plan.triggerTitle}. Add ${left} more paid item${left === 1 ? '' : 's'} to qualify, then choose your ${freeWord}.`;
      } else if (plan.paid > 0) {
        subtitleEl.textContent = `${badge} — you're qualified! Pick your ${freeWord} below.`;
      } else {
        subtitleEl.textContent = `${badge} — you just added ${plan.triggerTitle}. Pick your reward below.`;
      }
    }
    if (stepsEl) {
      if (total > 1) {
        let html = '';
        for (let i = 0; i < total; i++) {
          const isPaid = i < plan.paid;
          const label = isPaid ? `${ordinal(plan.units + i + 1)} item` : (plan.free > 1 ? `FREE ${i - plan.paid + 1}` : 'FREE');
          const cls = i < plan.done ? ' is-done' : (i === plan.done ? ' is-current' : '');
          html += `<span class="bogo-step${cls}"><span class="bogo-step-num">${i < plan.done ? '✓' : (i + 1)}</span>${escHtml(label)}</span>`;
        }
        stepsEl.innerHTML = html;
        stepsEl.hidden = false;
      } else {
        stepsEl.innerHTML = '';
        stepsEl.hidden = true;
      }
    }
  }

  // Render the grid for the current pick (paid → qualifying products, free → rewards).
  async function renderStep() {
    const plan = pickPlan;
    const grid = document.getElementById('bogo-cart-popup-grid');
    if (!plan || !grid) return;
    const total = plan.paid + plan.free;
    const stepIsPaid = plan.done < plan.paid;
    updateHeader(plan, stepIsPaid);
    if (total === 0) {
      grid.innerHTML = `<div class="bogo-cart-popup-empty"><strong>Your ${escHtml(plan.bogo.badge_text || 'BOGO')} offer is already complete</strong><br><br>You have enough qualifying items in your cart — the discount is applied automatically at checkout.</div>`;
      return;
    }
    if (plan.done >= total) return; // all picks done — the success message is on screen
    const handles = stepIsPaid ? paidPickHandles(plan.bogo) : (plan.bogo.reward_handles || []);
    const token = ++renderSeq;
    grid.innerHTML = `<div class="bogo-cart-popup-loading">Loading ${stepIsPaid ? '' : 'reward '}options…</div>`;

    // Lazy-fetch each pickable product. We invalidate the per-handle cache here so the data
    // reflects post-add inventory (Shopify .js endpoints are usually live; cache is mostly
    // useful for repeat opens in the same session). For each product, also fetch its excluded
    // variants metafield so we know if the customer is left with NO actual choices.
    productCache.clear();
    const products = await Promise.all(handles.map(h => fetchProduct(h)));
    const exclusionLists = await Promise.all(handles.map(h => fetchRewardExcludedVariants(h)));
    if (token !== renderSeq || pickPlan !== plan) return; // a newer render superseded this one

    // Filter rewards: a reward is "viable" if it has at least one variant that is BOTH
    // available AND not in its own excluded list. Out-of-stock rewards (or rewards where
    // every variant was excluded by the merchant) are filtered out.
    const viable = products.map((p, i) => {
      if (!p) return null;
      const excluded = new Set((exclusionLists[i] || []).map(String));
      const variantsLeft = (p.variants || []).filter(v => {
        if (v.available === false) return false;
        return !(excluded.has(String(v.id)) || excluded.has('gid://shopify/ProductVariant/' + v.id));
      });
      return variantsLeft.length > 0 ? { product: p, available_variants: variantsLeft.length } : null;
    }).filter(Boolean);

    if (!viable.length) {
      // Nothing is viable — either everything is out of stock, or the customer just took
      // the last unit of the only available product. Tell them clearly and don't leave them
      // staring at an empty popup.
      const triggerTitle = plan.triggerTitle;
      const justAddedTheLastOne = products.length === 1
        && products[0]
        && products[0].handle === plan.triggerHandle
        && (products[0].variants || []).every(v => v.available === false || v.inventory_quantity === 0);
      let msg;
      if (justAddedTheLastOne) {
        msg = `<strong>You just took the last one in stock</strong><br><br>Sorry — there's no other ${escHtml(triggerTitle)} available right now to pair with this one. The offer needs ${plan.bogo.buy_quantity + plan.bogo.get_quantity} separate items.<br><br>Skip for now, or browse for another product to pair with this one.`;
      } else if (stepIsPaid) {
        msg = `<strong>No qualifying item available right now</strong><br><br>All the other products in this offer are out of stock. Your ${escHtml(triggerTitle)} is in the cart — you can come back later if stock is replenished.`;
      } else {
        msg = `<strong>No reward currently available</strong><br><br>All eligible reward products are out of stock right now. Your ${escHtml(triggerTitle)} is in the cart — you can come back later if stock is replenished.`;
      }
      grid.innerHTML = `<div class="bogo-cart-popup-empty">${msg}</div>`;
      return;
    }

    // Same-set offers: Shopify's BxGy engine always discounts the LOWEST-priced eligible unit in
    // the bundle, so a "free" pick that costs more than the trigger / paid picks isn't actually
    // free — the customer would get the cheaper unit free and pay the difference. Work out that
    // cap and label pricier picks honestly ("SAVE x" + the top-up), cheapest-safe picks first.
    let cap = Infinity;
    if (!stepIsPaid && plan.bogo.same_set) {
      const trig = await fetchProduct(plan.triggerHandle);
      if (token !== renderSeq || pickPlan !== plan) return;
      const candidates = [trig && trig.price].concat(plan.paidPickPrices)
        .map(Number).filter(n => Number.isFinite(n) && n > 0);
      if (candidates.length) cap = Math.min.apply(null, candidates);
    }
    plan.cap = cap;
    const isCapped = v => Number.isFinite(cap) && Number(v.product.price) > cap;
    const sorted = Number.isFinite(cap)
      ? viable.slice().sort((a, b) => (isCapped(a) ? 1 : 0) - (isCapped(b) ? 1 : 0))
      : viable;
    const note = sorted.some(isCapped)
      ? `<div class="bogo-cart-popup-note">Shopify makes the lowest-priced item in your bundle free, so picks priced above ${formatBhd(cap)} show what you save instead.</div>`
      : '';
    grid.innerHTML = note + sorted.map(v => rewardCardHtml(v.product, stepIsPaid, cap)).join('');
  }

  // Card for one pickable product. Paid (qualifying) picks show the real price + "ADD";
  // free picks show the struck-out price + "FREE" (the discount lands at checkout).
  function rewardCardHtml(p, paidStep, cap) {
    const img = (p.featured_image || (p.images && p.images[0]) || '');
    const imgSrc = img ? (img.startsWith('//') ? 'https:' + img : img) : '';
    const variantCount = (p.variants || []).length;
    const variantsAvailable = (p.variants || []).filter(v => v.available !== false).length;
    const price = Number(p.price) || 0;
    const priceText = price ? formatBhd(price) : '';
    const multi = variantCount > 1;
    let meta, note = '';
    if (paidStep) {
      meta = `<span class="bogo-reward-card-price">${priceText}</span>
            <span class="bogo-reward-card-add-tag">ADD</span>`;
    } else if (Number.isFinite(cap) && price > cap) {
      meta = `<span class="bogo-reward-card-price">${priceText}</span>
            <span class="bogo-reward-card-save-tag">SAVE ${formatBhd(cap)}</span>`;
      note = `<div class="bogo-reward-card-variants">Pricier than your other items — you pay the ${formatBhd(price - cap)} difference</div>`;
    } else {
      meta = `<span class="bogo-reward-card-price-strike">${priceText}</span>
            <span class="bogo-reward-card-free-tag">FREE</span>`;
    }
    return `<button type="button" class="bogo-reward-card" data-bogo-reward-handle="${escAttr(p.handle)}">
        ${imgSrc ? `<img src="${escAttr(imgSrc)}" alt="" class="bogo-reward-card-img" loading="lazy">` : '<div class="bogo-reward-card-img"></div>'}
        <div class="bogo-reward-card-body">
          <div class="bogo-reward-card-title">${escHtml(p.title)}</div>
          <div class="bogo-reward-card-meta">
            ${meta}
          </div>
          ${note}
          ${multi ? `<div class="bogo-reward-card-variants">${variantsAvailable} option${variantsAvailable === 1 ? '' : 's'} — tap to choose</div>` : ''}
        </div>
      </button>`;
  }

  function formatBhd(cents) {
    // BHD: prices in /products.js come back in cents (×100), display as 3-decimal BHD
    const val = (parseFloat(cents) / 100).toFixed(3);
    return `${val} BHD`;
  }

  function escHtml(s) {
    const d = document.createElement('div'); d.textContent = s || ''; return d.innerHTML;
  }
  function escAttr(s) { return String(s || '').replace(/"/g, '&quot;'); }

  // ── Reward card click ──

  async function onRewardCardClick(e) {
    const card = e.target.closest('[data-bogo-reward-handle]');
    if (!card) return;
    const handle = card.dataset.bogoRewardHandle;
    // Re-fetch (no cache) so we get fresh availability after the trigger add reduced stock.
    productCache.delete(handle);
    const product = await fetchProduct(handle);
    if (!product) return;

    // Fetch this reward's excluded variants (from its own bogo:excluded_variants meta tag).
    // Filter them out of both the "available" check and the variant picker so the customer
    // can't pick a variant the merchant has explicitly excluded from the BOGO.
    const excludedIds = await fetchRewardExcludedVariants(handle);
    const excluded = new Set(excludedIds);
    const isExcluded = v => excluded.has(String(v.id)) || excluded.has('gid://shopify/ProductVariant/' + v.id);
    const filteredVariants = (product.variants || []).filter(v => !isExcluded(v));
    const availableVariants = filteredVariants.filter(v => v.available !== false);

    // No clickable variants at all → tell them and stay
    if (availableVariants.length === 0) {
      alert('Sorry, all variants of this product are currently out of stock.');
      return;
    }

    // Single-variant product (only one option after exclusions, available) → add directly,
    // no point showing a picker.
    if (filteredVariants.length === 1) {
      await addRewardToCart(availableVariants[0].id, product.title, availableVariants[0].price);
      return;
    }

    // Multi-variant: always show the picker so the customer can choose. Sold-out variants
    // appear greyed out and disabled, available ones are clickable — even if only one is
    // available (the customer sees what they're getting + sees what's NOT available).
    showVariantPicker({ ...product, variants: filteredVariants });
  }

  function showVariantPicker(product) {
    const picker = document.getElementById('bogo-variant-picker');
    const title = document.getElementById('bogo-variant-picker-title');
    const body = document.getElementById('bogo-variant-picker-body');
    if (!picker || !body) return;
    title.textContent = product.title;

    // Product-level fallback image (used when a variant has no featured_image)
    const fallbackImg = product.featured_image
      || (product.images && product.images[0])
      || '';
    const fallbackSrc = fallbackImg
      ? (typeof fallbackImg === 'string'
          ? (fallbackImg.startsWith('//') ? 'https:' + fallbackImg : fallbackImg)
          : (fallbackImg.src || ''))
      : '';

    body.innerHTML = (product.variants || []).map(v => {
      const disabled = v.available === false;
      // Each variant in /products/{handle}.js may carry its own featured_image — use it if present
      let imgUrl = '';
      if (v.featured_image && (v.featured_image.src || typeof v.featured_image === 'string')) {
        imgUrl = v.featured_image.src || v.featured_image;
      } else if (typeof v.image === 'string') {
        imgUrl = v.image;
      } else {
        imgUrl = fallbackSrc;
      }
      if (imgUrl && imgUrl.startsWith('//')) imgUrl = 'https:' + imgUrl;
      const thumb = imgUrl
        ? `<img src="${escAttr(imgUrl)}" alt="" class="bogo-variant-row-img" loading="lazy">`
        : '<div class="bogo-variant-row-img bogo-variant-row-img-placeholder"></div>';
      return `<button type="button" class="bogo-variant-row${disabled ? ' is-disabled' : ''}" data-bogo-variant-id="${escAttr(v.id)}" data-bogo-variant-price="${escAttr(v.price)}" ${disabled ? 'disabled' : ''}>
          ${thumb}
          <span class="bogo-variant-row-title">${escHtml(v.title || 'Default')}</span>
          <span class="bogo-variant-row-meta">${disabled ? 'Out of stock' : 'Pick this one'}</span>
        </button>`;
    }).join('');
    picker.hidden = false;
  }

  function hideVariantPicker() {
    const picker = document.getElementById('bogo-variant-picker');
    if (picker) picker.hidden = true;
  }

  async function onVariantPickerClick(e) {
    const btn = e.target.closest('[data-bogo-variant-id]');
    if (!btn || btn.disabled) return;
    const variantId = btn.dataset.bogoVariantId;
    const titleEl = document.getElementById('bogo-variant-picker-title');
    await addRewardToCart(variantId, titleEl ? titleEl.textContent : '', btn.dataset.bogoVariantPrice);
  }

  async function addRewardToCart(variantId, label, unitPrice) {
    const grid = document.getElementById('bogo-cart-popup-grid');
    const plan = pickPlan;
    if (plan) plan.adding = true;
    if (grid) grid.innerHTML = '<div class="bogo-cart-popup-loading">Adding to cart&hellip;</div>';
    // Remember the variant picker state so we can restore it on error
    const variantPicker = document.getElementById('bogo-variant-picker');
    const pickerWasOpen = variantPicker && !variantPicker.hidden;
    const pickerProductTitle = document.getElementById('bogo-variant-picker-title')?.textContent || '';
    const pickerBodyHtml = document.getElementById('bogo-variant-picker-body')?.innerHTML || '';
    hideVariantPicker();
    try {
      const formData = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.description || 'Could not add to cart');
      // Refresh the page's cart badge if Dawn pubsub is around (no-op on this theme since
      // PUB_SUB_EVENTS is undefined, but kept for any consumer that has set it up).
      try {
        if (window.PUB_SUB_EVENTS && window.publish) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bogo-popup', cartData: data });
        }
      } catch (e) {}
      // Direct cart-icon refresh — covers the case where PUB_SUB_EVENTS isn't wired up.
      refreshCartIcon();

      // Multi-pick offers (Buy 2 Get 1 etc.): count this pick and move on to the next step
      // instead of closing. The last pick shows the success message and closes as before.
      if (plan && plan === pickPlan) {
        plan.adding = false;
        plan.done += 1;
        const total = plan.paid + plan.free;
        const wasPaid = (plan.done - 1) < plan.paid;
        const price = Number(unitPrice);
        if (wasPaid && Number.isFinite(price) && price > 0) plan.paidPickPrices.push(price);
        // A "free" pick priced above the cap only earns the cap as a discount (see renderStep).
        const capped = !wasPaid && Number.isFinite(plan.cap) && Number.isFinite(price) && price > plan.cap;
        if (plan.done < total) {
          const nextIsPaid = plan.done < plan.paid;
          const next = nextIsPaid
            ? `Now pick your ${ordinal(plan.units + plan.done + 1)} item.`
            : (plan.free > 1 ? `Now pick your ${plan.free} FREE items.` : 'Now pick your FREE item.');
          const tag = wasPaid ? '' : (capped ? ` (${formatBhd(plan.cap)} off)` : ' (free)');
          if (grid) grid.innerHTML = `<div class="bogo-cart-popup-success"><strong>&#10003; Added ${escHtml(label)}${tag}</strong> ${escHtml(next)}</div>`;
          setTimeout(() => { if (pickPlan === plan) renderStep(); }, 900);
          return;
        }
        if (grid) {
          const badge = escHtml(plan.bogo.badge_text || 'BOGO');
          if (capped) {
            grid.innerHTML = `<div class="bogo-cart-popup-success"><strong>&#10003; All set!</strong> ${escHtml(label)} is in your cart — your ${badge} discount (${formatBhd(plan.cap)} off the lowest-priced item) is applied automatically at checkout.</div>`;
          } else if (plan.paid > 0) {
            grid.innerHTML = `<div class="bogo-cart-popup-success"><strong>&#10003; All set!</strong> ${escHtml(label)} is in your cart — your ${badge} discount is applied automatically at checkout.</div>`;
          } else {
            grid.innerHTML = `<div class="bogo-cart-popup-success"><strong>&#10003; Added!</strong> Your free ${escHtml(label)} is now in your cart with the discount applied.</div>`;
          }
        }
      } else if (grid) {
        grid.innerHTML = `<div class="bogo-cart-popup-success"><strong>&#10003; Added!</strong> Your free ${escHtml(label)} is now in your cart with the discount applied.</div>`;
      }
      setTimeout(closePopup, 1800);
      // If we're sitting on /cart (the popup was restored here after Boost SD's post-add
      // navigation), the cart UI was rendered with only the trigger product. Refresh the
      // page after the popup closes so the customer sees every item in their cart.
      if (location.pathname === '/cart' || location.pathname.startsWith('/cart')) {
        setTimeout(function() { location.reload(); }, 2000);
      }
    } catch (e) {
      if (plan) plan.adding = false;
      // Failure → show message + two recovery buttons so the customer isn't stuck.
      if (grid) {
        const canGoBackToPicker = pickerWasOpen && pickerBodyHtml.length > 0;
        grid.innerHTML = `<div class="bogo-cart-popup-empty">
          <strong>Couldn't add ${escHtml(label)}</strong><br><br>
          ${escHtml(e.message)}<br><br>
          <div class="bogo-popup-recover-actions">
            ${canGoBackToPicker ? '<button type="button" class="bogo-recover-btn" data-bogo-recover="picker">&larr; Pick a different variant</button>' : ''}
            <button type="button" class="bogo-recover-btn" data-bogo-recover="rewards">&larr; Pick a different product</button>
          </div>
        </div>`;
        const onRecover = (e2) => {
          const btn = e2.target.closest('[data-bogo-recover]');
          if (!btn) return;
          if (btn.dataset.bogoRecover === 'picker' && canGoBackToPicker) {
            // Restore the variant picker with the same product
            const titleEl = document.getElementById('bogo-variant-picker-title');
            const bodyEl = document.getElementById('bogo-variant-picker-body');
            if (titleEl) titleEl.textContent = pickerProductTitle;
            if (bodyEl) bodyEl.innerHTML = pickerBodyHtml;
            if (variantPicker) variantPicker.hidden = false;
          } else if (pickPlan) {
            // Re-render the current step's grid (keeps the picks already made)
            renderStep();
          } else if (lastOpenArgs) {
            // Reopen the full reward grid
            openPopup(lastOpenArgs.triggerHandle, lastOpenArgs.bogoData, lastOpenArgs.triggerProduct);
          }
        };
        grid.querySelectorAll('[data-bogo-recover]').forEach(b => b.addEventListener('click', onRecover));
      }
    }
  }

  function closePopup() {
    const popup = document.getElementById(POPUP_ID);
    if (popup) popup.hidden = true;
    popupOpen = false;
    lastClosedAt = Date.now();
    pickPlan = null;
    document.body.style.overflow = '';
    hideVariantPicker();
  }

  // ── Cart scan fallback ──
  async function scanCartForBogo() {
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      const lastItem = (cart.items || []).slice(-1)[0];
      if (lastItem) checkHandleForBogo(lastItem.handle, String(lastItem.variant_id));
    } catch (e) {}
  }

  // Expose for manual debugging / testing
  window.__bogoCartPopup = {
    open: (handle) => fetchProductMetafields(handle).then(d => d && openPopup(handle, d, productCache.get(handle))),
    close: closePopup,
    resetCooldown: () => { lastClosedAt = 0; },
    plan: () => pickPlan,          // current multi-pick plan (null when closed)
    computePlan,                   // pure: (bogo, cart) → {paid, free, units}
    withBxgyMeta,                  // (handle, bogoData) → bogo data merged with the trigger map
    parseBxgyFromText,             // pure: "B2G1" / "Buy 2 Get 1 Free" → {buy, get}
  };
})();
